import React from 'react';
import { useTranslation } from 'react-i18next';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import AppleIcon from '@mui/icons-material/Apple';
import WindowIcon from '@mui/icons-material/Window';
import { Box, Divider, Stack, Typography } from '@/shared/ui';

const KeyCap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 36,
      px: 1.25,
      py: 0.5,
      border: '1px solid',
      borderColor: 'primary.light',
      borderRadius: 1,
      bgcolor: 'common.white',
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: 'text.primary',
      lineHeight: 1.4,
    }}
  >
    {children}
  </Box>
);

const ShortcutRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  keys: React.ReactNode;
}> = ({ icon, label, keys }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
    <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>{icon}</Box>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72, fontWeight: 600 }}>
      {label}
    </Typography>
    {keys}
  </Stack>
);

const UnsupportedResolution: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#e9ebed',
        px: 3,
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ maxWidth: 520, textAlign: 'center' }}>
        <LaptopMacIcon sx={{ fontSize: 88, color: 'primary.dark' }} />

        <Box>
          <Typography variant="h5" component="h1" fontWeight={700} color="primary.dark" gutterBottom>
            {t('unsupportedResolution.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('unsupportedResolution.description', { brand: 'BUG DOS' })}
          </Typography>
        </Box>

        <Divider sx={{ width: '100%', borderColor: 'primary.light' }} />

        <Typography variant="body2" color="text.secondary">
          {t('unsupportedResolution.suggestion')}
        </Typography>

        <Stack spacing={1.5} sx={{ width: '100%' }}>
          <ShortcutRow
            icon={<AppleIcon fontSize="small" />}
            label={t('unsupportedResolution.macOs')}
            keys={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <KeyCap>⌘ {t('unsupportedResolution.command')}</KeyCap>
                <Typography component="span" color="text.secondary">
                  +
                </Typography>
                <KeyCap>−</KeyCap>
              </Stack>
            }
          />
          <ShortcutRow
            icon={<WindowIcon fontSize="small" />}
            label={t('unsupportedResolution.windows')}
            keys={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <KeyCap>{t('unsupportedResolution.ctrl')}</KeyCap>
                <Typography component="span" color="text.secondary">
                  +
                </Typography>
                <KeyCap>−</KeyCap>
              </Stack>
            }
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default UnsupportedResolution;
